import { lazy, type ComponentType } from "react";
import { Navigate, Route } from "react-router-dom";

import { MarketingLayout } from "../marketing/layouts/MarketingLayout";
import { MARKETING_LANGS, PAGE_PATHS, type MarketingPageId } from "../marketing/content/routes";
import { LegalPage } from "../features/legal/LegalPage";

function lazyPage<T extends Record<string, ComponentType>>(
  loader: () => Promise<T>,
  name: keyof T,
) {
  return lazy(() => loader().then((mod) => ({ default: mod[name] as ComponentType })));
}

const HomePage = lazyPage(() => import("../marketing/pages/HomePage"), "HomePage");
const OwnerAccountPage = lazyPage(() => import("../marketing/pages/OwnerAccountPage"), "OwnerAccountPage");
const ManagerAccountPage = lazyPage(() => import("../marketing/pages/ManagerAccountPage"), "ManagerAccountPage");
const ManagementPage = lazyPage(() => import("../marketing/pages/ManagementPage"), "ManagementPage");
const NetworkPage = lazyPage(() => import("../marketing/pages/NetworkPage"), "NetworkPage");
const SharingPage = lazyPage(() => import("../marketing/pages/SharingPage"), "SharingPage");
const RequestsPage = lazyPage(() => import("../marketing/pages/RequestsPage"), "RequestsPage");
const MessagingPage = lazyPage(() => import("../marketing/pages/MessagingPage"), "MessagingPage");
const OwnerPortalPage = lazyPage(() => import("../marketing/pages/OwnerPortalPage"), "OwnerPortalPage");
const PartnerAccountPage = lazyPage(() => import("../marketing/pages/PartnerAccountPage"), "PartnerAccountPage");
const PricingPage = lazyPage(() => import("../marketing/pages/PricingPage"), "PricingPage");
const AboutPage = lazyPage(() => import("../marketing/pages/AboutPage"), "AboutPage");
const ContactPage = lazyPage(() => import("../marketing/pages/ContactPage"), "ContactPage");
const HelpHomePage = lazyPage(() => import("../features/help/HelpHomePage"), "HelpHomePage");
const HelpCategoryPage = lazyPage(() => import("../features/help/HelpCategoryPage"), "HelpCategoryPage");
const HelpArticlePage = lazyPage(() => import("../features/help/HelpArticlePage"), "HelpArticlePage");

const PAGE_COMPONENTS: Partial<Record<MarketingPageId, ComponentType>> = {
  home: HomePage,
  ownerAccount: OwnerAccountPage,
  managerAccount: ManagerAccountPage,
  management: ManagementPage,
  network: NetworkPage,
  sharing: SharingPage,
  requests: RequestsPage,
  messaging: MessagingPage,
  ownerPortal: OwnerPortalPage,
  partnerAccount: PartnerAccountPage,
  pricing: PricingPage,
  about: AboutPage,
  contact: ContactPage,
};

function PrivacyPage() {
  return <LegalPage document="privacy" />;
}

function TermsPage() {
  return <LegalPage document="terms" />;
}

export function MarketingRouteTree() {
  return (
    <Route element={<MarketingLayout />}>
      {(Object.keys(PAGE_COMPONENTS) as MarketingPageId[]).flatMap((id) => {
        const Component = PAGE_COMPONENTS[id];
        if (!Component) return [];
        return MARKETING_LANGS.map((lang) => (
          <Route key={`${id}-${lang}`} path={PAGE_PATHS[id][lang]} element={<Component />} />
        ));
      })}
      {MARKETING_LANGS.map((lang) => (
        <Route key={`privacy-${lang}`} path={PAGE_PATHS.privacy[lang]} element={<PrivacyPage />} />
      ))}
      {MARKETING_LANGS.map((lang) => (
        <Route key={`terms-${lang}`} path={PAGE_PATHS.terms[lang]} element={<TermsPage />} />
      ))}
      {MARKETING_LANGS.map((lang) => {
        const base = PAGE_PATHS.help[lang];
        return [
          <Route key={`help-${lang}`} path={base} element={<HelpHomePage />} />,
          <Route key={`help-cat-${lang}`} path={`${base}/:categorySlug`} element={<HelpCategoryPage />} />,
          <Route
            key={`help-art-${lang}`}
            path={`${base}/:categorySlug/:articleSlug`}
            element={<HelpArticlePage />}
          />,
        ];
      })}
      <Route path="/fonctionnement" element={<Navigate to="/" replace />} />
      <Route path="/en/how-it-works" element={<Navigate to="/en" replace />} />
      <Route path="/es/como-funciona" element={<Navigate to="/es" replace />} />
      <Route path="/politica-de-privacidad" element={<Navigate to="/es/privacy" replace />} />
      <Route path="/condiciones-de-uso" element={<Navigate to="/es/terms" replace />} />
      <Route path="/terminos-de-uso" element={<Navigate to="/es/terms" replace />} />
    </Route>
  );
}
